<template>
  <nav class="menu-panel" :class="{ 'is-sheet': variant === 'sheet' }" :aria-label="$t('assistantCatHome.openMenu')">
    <div v-for="(section, sectionIndex) in sections" :key="sectionIndex" class="menu-panel__section">
      <div v-for="item in section" :key="item.action" class="menu-panel__item">
        <button
          type="button"
          class="menu-panel__main"
          :class="{ 'menu-panel__main--with-trailing-action': Boolean(trailingActionOf(item)) }"
          @click="$emit('action', item.action)"
        >
          <span class="menu-panel__label">{{ $t(`assistantCatHome.menu.${item.action}`) }}</span>
          <span v-if="!trailingActionOf(item)" class="menu-panel__trailing">
            <cat-icon
              v-if="item.trailing === 'catThumbnail'"
              size="small"
              :clickable="false"
              :writer-type="writerType"
              class="menu-panel__cat"
            />
            <icon-chevron-right v-else class="menu-panel__chevron" />
          </span>
        </button>
        <button
          v-if="trailingActionOf(item)"
          type="button"
          class="menu-panel__trailing-button"
          :aria-label="$t(`assistantCatHome.menu.${trailingActionOf(item)}`)"
          @click.stop="$emit('action', trailingActionOf(item))"
        >
          <img class="menu-panel__mode-icon" :src="trailingIconSrc(item)" alt="" />
        </button>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import Vue from "vue";
import CatIcon from "@/components/atoms/CatIcon.vue";
import {
  ASSISTANT_CAT_HOME_MENU_SECTIONS,
  AssistantCatHomeAction,
  AssistantCatHomeMenuItem,
  assistantCatHomeTrailingAction,
} from "@/constants/assistantCatHome";
import dryIcon from "@/assets/img/icon/mode/dry.svg";
import fumufumuIcon from "@/assets/img/icon/mode/fumufumu.svg";
import normalIcon from "@/assets/img/icon/mode/normal.svg";
import wakuwakuIcon from "@/assets/img/icon/mode/wakuwaku.svg";
import { FeedbackManuscriptMode, GenerateSummaryMode } from "@/lib/models/assist";
import IconChevronRight from "~icons/figma/ChevronRight";

export default Vue.extend({
  name: "AssistantCatHomeMenuPanel",
  components: {
    IconChevronRight,
    CatIcon,
  },
  props: {
    variant: {
      type: String,
      default: "panel",
    },
    writerType: {
      type: String,
      default: null,
    },
    feedbackManuscriptMode: {
      type: String,
      default: FeedbackManuscriptMode.NORMAL,
    },
    generateSummaryMode: {
      type: String,
      default: GenerateSummaryMode.WAKUWAKU,
    },
  },
  data() {
    return {
      sections: ASSISTANT_CAT_HOME_MENU_SECTIONS,
    };
  },
  computed: {
    feedbackModeIconSrc(): string {
      return this.feedbackManuscriptMode === FeedbackManuscriptMode.DRY ? dryIcon : normalIcon;
    },
    summaryModeIconSrc(): string {
      return this.generateSummaryMode === GenerateSummaryMode.FUMUFUMU ? fumufumuIcon : wakuwakuIcon;
    },
  },
  methods: {
    trailingActionOf(item: AssistantCatHomeMenuItem): AssistantCatHomeAction | null {
      return assistantCatHomeTrailingAction(item.trailing);
    },
    trailingIconSrc(item: AssistantCatHomeMenuItem): string {
      if (item.trailing === "feedbackModeIcon") {
        return this.feedbackModeIconSrc;
      }
      if (item.trailing === "summaryModeIcon") {
        return this.summaryModeIconSrc;
      }
      return "";
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.menu-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  width: 323px;
  max-width: 100%;
  padding: 16px;
  max-height: var(--assistant-cat-home-side-height, calc(100vh - 120px));
  overflow-x: hidden;
  overflow-y: auto;
  background: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);

  &.is-sheet {
    width: 100%;
    max-height: none;
    padding: 0;
    overflow: visible;
    background: transparent;
    border-radius: 0;
    box-shadow: none;

    .menu-panel__main,
    .menu-panel__trailing-button {
      font-size: var(--font-size-md);
    }
  }
}

.menu-panel__section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  .is-sheet & {
    padding-bottom: 0;
    border-bottom: none;

    &:not(:last-child) {
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid var(--color-border);
    }
  }
}

.menu-panel__item {
  position: relative;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  min-height: 40px;
  border-radius: var(--radius-sm);
  background: var(--color-surface-base);
  color: var(--color-text-normal);

  @include hover-overlay;
}

.menu-panel__main,
.menu-panel__trailing-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.menu-panel__main {
  flex: 1 1 auto;
  justify-content: space-between;
  gap: 8px;
  height: 100%;
  padding: 8px 16px;
  text-align: left;

  &--with-trailing-action {
    padding-right: 0;
  }
}

.menu-panel__trailing-button {
  flex-shrink: 0;
  height: 100%;
  padding: 8px 16px 8px 8px;
}

.menu-panel__label {
  flex: 1 1 auto;
  min-width: 0;
}

.menu-panel__trailing {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: var(--color-text-normal);
}

.menu-panel__mode-icon {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.menu-panel__chevron {
  display: block;
  width: 16px;
  height: 16px;
  font-size: 16px;
}

.menu-panel__cat {
  width: 22px;
  height: 24px;

  ::v-deep .cat-icon {
    width: 22px;
    height: 24px;
  }
}
</style>
